import { cn } from '@/shared/helpers/classnames'
import { getTypedBlocksByTypes } from '@/shared/helpers/compoundPattern'

import { Children, type FC, isValidElement, useCallback, useId, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { SideMenuToggle } from './SideMenuToggle'
import { SideMenuTrigger } from './SideMenuTrigger'
import { SideMenuContext } from './context'
import type { SideMenuFooterProps, SideMenuProps } from './types'
import { useCloseOnRouteChange } from './useCloseOnRouteChange'
import { useEscapeToClose } from './useEscapeToClose'
import { useLockScrollWhenOpen } from './useLockScrollWhenOpen'

type SideMenuSlots = {
  SideMenuFooter: SideMenuFooterProps
}

const isFooter = (child: unknown): boolean =>
  isValidElement(child) && (child.type as { displayName?: string }).displayName === 'SideMenuFooter'

/**
 * SideMenu — fixed left-side navigation for the app shell.
 *
 * The Ardian logo (square mark + wordmark) is rendered automatically as the
 * header; the mobile hamburger Trigger is also auto-rendered (visible only
 * below the `lg:` breakpoint). Consumers compose the body items.
 *
 * Compound + Slots pattern. Sub-components: Item, Group, SubItem, Separator,
 * Footer, Action. Order of body children is flexible.
 *
 * Responsive: pure CSS via Tailwind `lg:` (≥1024px).
 * - Desktop: 52px rail by default; hover expands a temporary preview;
 *   internal toggle pins the menu open at 280px.
 * - Mobile: hidden by default; auto-rendered hamburger opens a slide-in
 *   drawer with backdrop. Tap-outside or Escape closes it.
 *
 * Supports controlled (`open` + `onOpenChange`) and uncontrolled
 * (`defaultOpen`) modes.
 */
export const SideMenu: FC<SideMenuProps> = ({
  children,
  defaultOpen = false,
  open,
  onOpenChange,
}) => {
  const { t } = useTranslation()
  const navId = useId()
  const isControlled = open !== undefined
  const [internalOpen, setInternalOpen] = useState(defaultOpen)
  const isOpen = isControlled ? open : internalOpen

  const setOpen = useCallback(
    (next: boolean) => {
      if (!isControlled) setInternalOpen(next)
      onOpenChange?.(next)
    },
    [isControlled, onOpenChange],
  )

  useEscapeToClose(isOpen, setOpen)
  useLockScrollWhenOpen(isOpen)
  useCloseOnRouteChange(isOpen, setOpen)

  const contextValue = useMemo(() => ({ isOpen, setOpen, navId }), [isOpen, setOpen, navId])

  // Footer is a single-slot extraction (DS helper). Body is everything else
  // in declared JSX order — Items/Groups/Separators interleave, so we
  // filter by displayName rather than bucket by type.
  const { SideMenuFooter: footer } = getTypedBlocksByTypes<SideMenuSlots>(children)
  const body = Children.toArray(children).filter(child => !isFooter(child))

  return (
    <SideMenuContext.Provider value={contextValue}>
      <nav
        id={navId}
        aria-label={t('sideMenu.aria.mainNavigation')}
        className={cn('side-menu', isOpen && 'side-menu-open')}
      >
        <div className="side-menu-header">
          <span className="side-menu-logo-square">
            <img src="/img/ardian_logo_lettre.svg" alt="" />
          </span>
          <img src="/img/ardian_logo.svg" alt="Ardian" className="side-menu-logo-text" />
        </div>
        <div className="side-menu-body">{body}</div>
        <div className="side-menu-footer">
          {footer}
          <SideMenuToggle />
        </div>
      </nav>
      {isOpen && (
        <button
          type="button"
          aria-label={t('sideMenu.aria.closeNavigation')}
          className="side-menu-backdrop lg:hidden"
          onClick={() => setOpen(false)}
        />
      )}
      <SideMenuTrigger />
    </SideMenuContext.Provider>
  )
}

SideMenu.displayName = 'SideMenu'
